<!--
 * Path: src/core/components/dialogs/OpenProjectDialog.vue
 * Task: Selection modal to load existing project catalogs with icon support.
 * Refactored: Added selection slot and fixed metadata icon/color resolution.
-->
<template>
  <BaseDialog
    :model-value="projectStore.isOpenProjectDialogOpen"
    @update:model-value="handleDialogUpdate"
    title="Open Project"
    max-width="600"
    @close="handleClose"
  >
    <p class="mb-4 text-medium-emphasis">Select a project you want to load.</p>

    <!-- Standard Vuetify select for project list selection -->
    <v-select
      v-model="selectedProject"
      :items="projectStore.projectOptions"
      label="Select a project"
      :loading="projectStore.isLoading"
      item-title="title"
      return-object
      variant="outlined"
      hide-details
    >
      <!-- Selected item presentation (inside the input field) -->
      <template v-slot:selection="{ item }">
        <div class="d-flex align-center">
          <v-avatar
            :color="getProjectColor(item.raw)"
            size="24"
            style="border-radius: 6px;"
            class="me-3"
          >
            <v-icon color="white" size="16">
              {{ getProjectIcon(item.raw) }}
            </v-icon>
          </v-avatar>
          <span>{{ item.raw.title }}</span>
        </div>
      </template>

      <!-- Dropdown list items -->
      <template v-slot:item="{ props: itemProps, item }">
        <v-list-item
          v-bind="itemProps"
          :title="item.raw.title"
        >
          <template v-slot:prepend>
            <v-avatar
              :color="getProjectColor(item.raw)"
              size="24"
              style="border-radius: 6px;"
              class="me-3"
            >
              <v-icon color="white" size="16">
                {{ getProjectIcon(item.raw) }}
              </v-icon>
            </v-avatar>
          </template>
        </v-list-item>
      </template>
    </v-select>

    <template #actions>
      <v-btn
        variant="text"
        @click="handleClose"
      >
        Cancel
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        variant="flat"
        @click="handleProjectSelect"
        :disabled="!selectedProject"
      >
        Open
      </v-btn>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useProjectStore, type LoadedProject } from '@/store/project';
import { useAppStyle } from '@/core/composables/useAppStyle';
import BaseDialog from './BaseDialog.vue';

const projectStore = useProjectStore();
const { getProjectTypeStyle, resolveHexColor } = useAppStyle();

const selectedProject = ref<LoadedProject | null>(null);

// Properly resolve custom icons or fallback to visibility style
const getProjectIcon = (project: LoadedProject): string => {
  if (project.icon) return project.icon;
  const meta = projectStore.projectsMetadata?.[project.value];
  return getProjectTypeStyle(meta?.visibility || 'restricted').icon;
};

// Properly resolve custom colors or fallback to visibility style
const getProjectColor = (project: LoadedProject): string => {
  if (project.color) return resolveHexColor(project.color);
  const meta = projectStore.projectsMetadata?.[project.value];
  return resolveHexColor(getProjectTypeStyle(meta?.visibility || 'restricted').color);
};

const handleDialogUpdate = (isOpen: boolean) => {
  projectStore.isOpenProjectDialogOpen = isOpen;
  if (isOpen) {
    selectedProject.value = null;
  }
};

const handleClose = () => {
  projectStore.isOpenProjectDialogOpen = false;
  selectedProject.value = null;
};

const handleProjectSelect = () => {
  if (selectedProject.value) {
    projectStore.openProject(selectedProject.value.value);
  }
};
</script>
